import { NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, ElementRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fadeInFadeOut } from '@animations/fade';
import { BookingFailedViewComponent } from '@components/booking-failed-view/booking-failed-view.component';
import { BookingQuestionsViewComponent } from '@components/booking-questions-view/booking-questions-view.component';
import { BookingSuccessViewComponent } from '@components/booking-success-view/booking-success-view.component';
import { BookingSummaryViewComponent } from '@components/booking-summary-view/booking-summary-view.component';
import { MeetingTypeSelectionComponent } from '@components/meeting-type-selection/meeting-type-selection.component';
import { PersonalFormViewComponent } from '@components/personal-form-view/personal-form-view.component';
import { ServicesSelectionComponent } from '@components/services-selection/services-selection.component';
import { SlotsSelectionComponent } from '@components/slots-selection/slots-selection.component';
import { StoreSelectionComponent } from '@components/store-selection/store-selection.component';
import { WorkersSelectionComponent } from '@components/workers-selection/workers-selection.component';
import { BOOKING_PAGE_VIEWS, BOOKING_REDIRECTION_VIEWS, CALENSO_SCROLL_EVENT, MEETING_TYPE_ID, PAYMENT_TYPES, SECTION_PRECEDENCE } from '@constants/app.constants';
import { WINDOW_LISTENER_CONSTANTS } from '@constants/window-listener.constants';
import { ADYEN_HOSTED } from '@constants/adyen-hosted.constants';
import { AppointmentBookingModel, BookedAppointment, BookingPageViews } from '@models/appointment-booking.model';
import { AppointmentReservationBody } from '@models/appointment-reservation.model';
import { PartnerDbModel, TokenType } from '@models/partner-db.model';
import { TimezoneDbModel } from '@models/timezone.model';
import { WidgetTemplateModel } from '@models/widget-template.model';
import { AdyenResume } from '@models/adyen-resume.model';
import { AdyenResumeService } from '@services/feature/adyen-resume.service';
import { BookingService } from '@services/feature/booking.service';
import { CustomEventService } from '@services/feature/custom-event.service';
import { TimezoneService } from '@services/feature/timezone.service';
import { WidgetService } from '@services/feature/widget.service';
import { DateUtilService } from '@services/utils/date-util.service';
import { FeatureService } from '@services/utils/feature.service';
import { GoogleAnalyticsService } from '@services/utils/google-analytics.service';
import { HostPageScrollService } from '@services/utils/host-page-scroll.service';
import { LoggerService } from '@services/utils/logger.service';
import { SwRouteService } from '@services/utils/sw-route.service';
import { GtmOperationComponent } from '@ui-lib/gtm-operation/gtm-operation.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-booking',
  templateUrl: './booking.component.html',
  styleUrls: ['./booking.component.scss'],
  animations: [fadeInFadeOut],
  imports: [
    BookingQuestionsViewComponent,
    MeetingTypeSelectionComponent,
    StoreSelectionComponent,
    ServicesSelectionComponent,
    WorkersSelectionComponent,
    SlotsSelectionComponent,
    PersonalFormViewComponent,
    BookingSummaryViewComponent,
    BookingSuccessViewComponent,
    BookingFailedViewComponent,
    GtmOperationComponent,
    NgClass,
    TranslateModule,
  ]
})
export class BookingComponent implements OnInit {

  private googleAnalyticsService = inject(GoogleAnalyticsService);
  protected bookingService = inject(BookingService);
  protected widgetService = inject(WidgetService);
  private timezoneService = inject(TimezoneService);
  private destroyRef = inject(DestroyRef);
  private customEventService = inject(CustomEventService);
  private featureService = inject(FeatureService);
  private dateUtilService = inject(DateUtilService);
  private swRouteService = inject(SwRouteService);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private hostPageScrollService = inject(HostPageScrollService);
  private adyenResumeService = inject(AdyenResumeService);

  /** A verified Adyen return this load came with, applied once the partner is known. */
  private adyenReturn: AdyenResume | null = null;
  /** This page is an Adyen payment tab sent back without the record of its payment: no fresh booking here. */
  protected showAdyenPaymentReceived = false;
  protected readonly adyenPaymentReceivedKey = ADYEN_HOSTED.I18N.PAYMENT_RECEIVED;

  protected readonly bookingPageViews = BOOKING_PAGE_VIEWS;
  protected readonly meetingTypeId = MEETING_TYPE_ID;

  protected counter = 0;
  protected partnerData: PartnerDbModel;
  protected widgetTemplates: WidgetTemplateModel[] = [];
  protected isFirstBookingQuestionActive = true;
  protected isLastBookingQuestionActive = false;
  // Dedicated state pair for the BEFORE-questions mount. In the combined
  // before+auto-load mode BOTH question mounts coexist; sharing one first/last pair meant
  // finishing the BEFORE step (which sets isLast=true for its own back-nav re-entry) made the
  // AFTER mount open on its LAST question, silently skipping the preceding ones.
  protected isFirstBeforeBookingQuestionActive = true;
  protected isLastBeforeBookingQuestionActive = false;
  protected selectedView: BookingPageViews = BOOKING_PAGE_VIEWS.STORE_VIEW;
  protected selectedViewPrecedence = SECTION_PRECEDENCE.STORE_VIEW;
  protected showOnePageUiUpToViewPrecedence: number;
  protected sectionPrecedence = SECTION_PRECEDENCE;
  protected disableAnimation = false;  // Set this to true when you want to disable the animation.
  protected timezones = new Array<TimezoneDbModel>();
  protected showStripeRedirectionLoader = false;

  constructor() {
    if (this.bookingService.isBeforeQuestionsMode()) {
      this.bookingService.isDefaultPage = true;
      this.selectedView = BOOKING_PAGE_VIEWS.BOOKING_QUESTIONS_VIEW_BEFORE;
      this.selectedViewPrecedence = 0;
    } else if (this.bookingService.isMeetingTypeStepInFlow()) {
      this.selectedView = BOOKING_PAGE_VIEWS.MEETING_TYPE_VIEW;
      this.selectedViewPrecedence = SECTION_PRECEDENCE.MEETING_TYPE_VIEW;
    }

    this.bookingService.initialSmartWidgetView = this.selectedView;
  }

  ngOnInit(): void {
    if (this.widgetService.isWebComponentMode) {
      this.hostPageScrollService.attach(this.elementRef.nativeElement, this.destroyRef);
    }

    // Check for Stripe redirect (immediate check from iframe URL)
    if (this.swRouteService.isStripeRedirect) {
      this.handleStripeRedirectReturn();
    }

    // Subscribe to late Stripe redirect detection (from parent URL via postMessage)
    // This handles the race condition where parent URL arrives after this initial check
    this.swRouteService.stripeRedirectDetected$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        LoggerService.log('[Stripe Redirect] Late detection triggered - handling redirect');
        this.handleStripeRedirectReturn();
      });

    // Read before anything touches the booking, and before any payment pane can build a new return URL.
    this.detectAdyenReturn();

    this.initializeBookingWidget(this.widgetService.partnerUUID);
  }

  /**
   * The return parameters are removed from the address bar here. Web component only - the iframe
   * embed always pays in a popup and its frame never navigates. Either way the read drops an expired
   * record, so a parked booking never outlives its session on a shared device.
   */
  private detectAdyenReturn(): void {
    if (this.widgetService.isWebComponentMode) {
      this.adyenReturn = this.adyenResumeService.detectReturn();
      this.showAdyenPaymentReceived = this.adyenResumeService.orphanHostedReturn;
    } else {
      this.adyenResumeService.read();
    }
  }

  /**
   * Back from an Adyen payment that left the page (redirect 3DS, or the hosted page in this tab):
   * the booking the shopper was paying for is restored from the record and the summary opens, where
   * the payment pane finishes the payment and books - once - exactly as if it had never left.
   */
  private handleAdyenReturn(): void {
    const resume = this.adyenReturn;
    this.adyenReturn = null;
    if (!resume) {
      return;
    }

    const booking = resume.record.booking;
    const data = this.bookingService.selectedBookingData;
    data.cartItems = booking.cartItems ?? [];
    data.selectedUser = booking.selectedUser;
    data.selectedCustomFieldValues = booking.selectedCustomFieldValues;
    data.selectedCoupon = booking.selectedCoupon;
    data.totalBookingAmount = booking.totalBookingAmount;
    data.totalDiscount = booking.totalDiscount;
    data.subTotal = booking.subTotal;
    data.timezone = booking.timezone;
    data.currentSelectedAppointmentState = booking.currentSelectedAppointmentState;
    data.paymentMethodData.paymentType = PAYMENT_TYPES.ADYEN;
    data.paymentMethodData.adyenPaymentDetails = undefined;

    if (!data.cartItems.length) {
      LoggerService.error('[Adyen return] The stored booking has no cart items - nothing to resume');
      this.adyenResumeService.clear();
      return;
    }

    this.adyenResumeService.setPendingResume(resume);
    this.gotoView({ view: BOOKING_PAGE_VIEWS.BOOKING_SUMMARY_VIEW, isUserInteracted: false });
  }

  /**
   * Handle return from Stripe 3DS/SCA redirect.
   * Restores booking data from localStorage and completes the booking.
   */
  private handleStripeRedirectReturn(): void {
    const redirectedPaymentIntent = this.swRouteService.redirectedPaymentIntent;
    const stripeRedirectionData = this.swRouteService.stripeRedirectionData;

    if (redirectedPaymentIntent && stripeRedirectionData) {
      LoggerService.log('[Stripe Redirect] Handling redirect return with payment intent:', redirectedPaymentIntent);

      // Show loading state
      this.showStripeRedirectionLoader = true;

      // Restore booking data from localStorage
      const { selectedBookingData } = stripeRedirectionData;

      // Restore the booking service data
      this.bookingService.partnerData = stripeRedirectionData.partnerData;
      this.bookingService.selectedBookingData.cartItems = selectedBookingData.cartItems;
      this.bookingService.selectedBookingData.selectedUser = selectedBookingData.selectedUser;
      this.bookingService.selectedBookingData.selectedCustomFieldValues = selectedBookingData.selectedCustomFieldValues;
      this.bookingService.selectedBookingData.selectedCoupon = selectedBookingData.selectedCoupon;
      this.bookingService.selectedBookingData.paymentMethodData = selectedBookingData.paymentMethodData;
      this.bookingService.selectedBookingData.totalBookingAmount = selectedBookingData.totalBookingAmount;
      this.bookingService.selectedBookingData.totalDiscount = selectedBookingData.totalDiscount;
      this.bookingService.selectedBookingData.subTotal = selectedBookingData.subTotal;
      this.bookingService.selectedBookingData.vatAmount = selectedBookingData.vatAmount;
      this.bookingService.selectedBookingData.timezone = selectedBookingData.timezone;
      this.bookingService.selectedBookingData.captcha = selectedBookingData.captcha;
      this.bookingService.selectedBookingData.currentSelectedAppointmentState = selectedBookingData.currentSelectedAppointmentState;

      // Save restored user data to standard localStorage so it persists for "Book Another"
      this.bookingService.setAppointmentSelectedUserInLocalStorage(selectedBookingData.selectedUser);

      // Set the Stripe payment method ID from the redirected payment intent
      this.bookingService.selectedBookingData.paymentMethodData.stripePaymentMethodId = redirectedPaymentIntent;

      // Proceed with booking directly
      this.processStripeRedirectBooking(stripeRedirectionData);
    } else {
      LoggerService.error('[Stripe Redirect] Payment intent exists but stored booking data is missing');
      this.showStripeRedirectionLoader = false;
    }
  }

  /**
   * Process booking after Stripe redirect return.
   * Uses the pre-built booking payload stored before redirect.
   */
  private processStripeRedirectBooking(stripeRedirectionData: Record<string, unknown>): void {
    // Use the pre-built booking payload directly from localStorage
    const bookingItems: AppointmentBookingModel[] = stripeRedirectionData.bookingPayload as AppointmentBookingModel[];

    // Update stripe_payment_intent_id with the confirmed payment intent from redirect
    const redirectedPaymentIntent = this.swRouteService.redirectedPaymentIntent;
    for (const item of bookingItems) {
      item.stripe_payment_intent_id = redirectedPaymentIntent;
    }

    LoggerService.log('[Stripe Redirect] Using pre-built booking payload with payment intent:', redirectedPaymentIntent);

    this.googleAnalyticsService.emitAppointmentBookingEvent('appointment_booking');
    this.bookingService.lastBookedAppointments = [];

    this.bookingService.bookAppointments(bookingItems).subscribe({
      next: (result: { success: boolean, errors: { message: string }[], appointments?: BookedAppointment[] }) => {
        // Clear the stored redirect data
        this.swRouteService.clearStripeRedirectionData();
        this.showStripeRedirectionLoader = false;

        if (result.success) {
          LoggerService.log('[Stripe Redirect] Booking completed successfully');
          this.bookingService.lastBookedAppointments = result.appointments ?? [];
          this.bookingService.resetBookingDataAfterBooking();
          this.selectedView = BOOKING_PAGE_VIEWS.BOOKING_SUCCESS_VIEW;
          this.selectedViewPrecedence = SECTION_PRECEDENCE[BOOKING_PAGE_VIEWS.BOOKING_SUCCESS_VIEW];

          // Handle external redirect if configured
          if (this.featureService.hasFeature('is_external_success_page_forwarding_enabled')) {
            if (
              this.bookingService.redirectAfterBookingEnabled &&
              this.bookingService.successfulBookingRedirectUrl &&
              this.bookingService.redirectAfterBookingAction === BOOKING_REDIRECTION_VIEWS.BOOKING_SUMMARY_VIEW
            ) {
              this.bookingService.redirectToSuccessPage();
            }
          }
        } else {
          LoggerService.error('[Stripe Redirect] Booking failed');
          this.selectedView = BOOKING_PAGE_VIEWS.BOOKING_FAILED_VIEW;
          this.selectedViewPrecedence = SECTION_PRECEDENCE[BOOKING_PAGE_VIEWS.BOOKING_FAILED_VIEW];
        }
      },
      error: (error: HttpErrorResponse) => {
        LoggerService.error('[Stripe Redirect] Booking error: ' + JSON.stringify(error));
        this.swRouteService.clearStripeRedirectionData();
        this.showStripeRedirectionLoader = false;
        this.selectedView = BOOKING_PAGE_VIEWS.BOOKING_FAILED_VIEW;
        this.selectedViewPrecedence = SECTION_PRECEDENCE[BOOKING_PAGE_VIEWS.BOOKING_FAILED_VIEW];
      }
    });
  }

  private initializeBookingWidget(partnerUUID: string): void {
    this.bookingService.initializeBookingWidget(partnerUUID, TokenType.publicSmartwidget).subscribe({
      next: partner => {
        if (partner) {
          this.partnerData = partner;
          if (this.partnerData?.adobe_launch_tracking_url && this.partnerData?.adobe_launch_tracking_url?.trim() !== '') {
            this.loadAdobeAnalytics(this.partnerData.adobe_launch_tracking_url);
          }

          if (this.featureService.hasFeature('is_timezone_enabled')) {
            this.partnerData.is_timezone_enabled = 1;
          }

          this.bookingService.partnerData = this.partnerData;
          this.googleAnalyticsService.partnerSubscriptionPlan = this.partnerData?.subscription;
          this.dateUtilService.setTimeFormatFromPartner(this.partnerData.widget_time_format);
          this.getTimezones();
          this.preinitializeWidgetContextValues();
          // After the context prefill, so the restored booking is what the summary shows.
          this.handleAdyenReturn();
          this.getWidgetTemplates();
          if (this.widgetService.widgetConf?.context?.enable_one_page_ui) {
            this.disableAnimation = true;
            // Set default max one page UI for view to SLOTS_VIEW
            this.showOnePageUiUpToViewPrecedence = SECTION_PRECEDENCE[BOOKING_PAGE_VIEWS.SLOTS_VIEW];
          }
        }
      }
    });
  }

  private loadAdobeAnalytics(adobe_launch_tracking_url: string): void {
    try {
      const script = document.createElement('script');
      script.src = adobe_launch_tracking_url;
      script.async = true;
      document.head.appendChild(script);
      LoggerService.log('Adobe Launch tracking script added.');
    } catch (error) {
      LoggerService.warn('Error appending dobe Launch tracking script');
      LoggerService.error(error);
    }
  }

  private preinitializeWidgetContextValues(): void {
    this.bookingService.loadAppointmentStateFromLocalStorage();
    this.bookingService.preInitializeWidgetContextValues(this.widgetService.widgetConf?.context, this.widgetService.customerConfData);
    this.bookingService.loadAppointmentSelectedUserFromLocalStorage();
    this.bookingService.preInitializeWidgetUserData(this.widgetService.customerConfData);
  }

  private getWidgetTemplates(): void {
    this.bookingService.getWidgetTemplates().subscribe({
      next: templates => {
        this.widgetTemplates = templates;
        this.bookingService.widgetTemplates = this.widgetTemplates;
      },
      error: (error: HttpErrorResponse) => LoggerService.error(error)
    });
  }

  /**
 * Navigates to the selected booking view and handles:
 * - Parent-child communication
 * - Scroll into view
 * - Service data preparation
 * - Conditional appointment reservation
 */
  protected gotoView(data: { view: BookingPageViews; isUserInteracted: boolean }): void {
    // Update selected view and its precedence
    this.selectedView = data.view;
    this.selectedViewPrecedence = SECTION_PRECEDENCE[data.view];

    // Notify parent window about view change (used for communication with external site/container)
    if (this.widgetService?.parentToChildOriginEvent) {
      this.widgetService.sendMessageToMainJs(this.widgetService.parentToChildOriginEvent, {
        eventName: WINDOW_LISTENER_CONSTANTS.PARENT_BOOKING_WEBSITE_EVENT,
        name: 'Child To Parent Communication Done',
        view: data.view,
      });
    }


    // Handle scroll to view if user interacted manually
    if (data.isUserInteracted) {
      setTimeout(() => {
        const element = document.getElementById(data.view);
        const parentOrigin = window.location.ancestorOrigins?.[0];

        // No parent frame in WC mode — this scroll event would post to the host page itself.
        if (element && !this.widgetService.isWebComponentMode) {
          window.parent.postMessage(
            {
              eventName: CALENSO_SCROLL_EVENT,
              data: {
                top: element.getBoundingClientRect().top
              },
            },
            parentOrigin || '*'
          );
        }
      }, 1000); // delay to ensure DOM is ready
    }

    // In WC mode the widget scrolls the host page itself. Deliberately not gated on isUserInteracted:
    // the last step of an auto-skip chain can be programmatic; the service only acts after a user gesture.
    if (this.widgetService.isWebComponentMode) {
      this.hostPageScrollService.requestStep(() => this.findScrollableSection(data.view));
    }

    // Handle auto-reservation of appointments if feature is enabled and view is changing to summary/questions
    const isReservationRequired =
      Number(this.partnerData.is_appointment_reservation_enabled) === 1 &&
      ([
        this.bookingPageViews.BOOKING_SUMMARY_VIEW,
        this.bookingPageViews.BOOKING_QUESTIONS_VIEW_AFTER
      ] as BookingPageViews[]).includes(data.view)
    if (isReservationRequired) {
      const appointments: AppointmentReservationBody[] = [];

      // Prepare list of appointments that are not yet reserved
      this.bookingService.selectedBookingData.cartItems.forEach((appointment, index) => {
        if (!appointment?.isAppointmentReserved) {
          appointments.push({
            appointment_service_id: appointment.serviceId,
            worker_id: appointment.workerId,
            start_time: appointment.startTime,
            end_time: appointment.endTime,
            timezone: this.bookingService.selectedBookingData?.timezone || this.timezoneService.localTimezone
          });

          // if timezone feature is not enabled, don't send timezone to the API
          if (!this.partnerData?.is_timezone_enabled) {
            delete appointments[index].timezone;
          }
        }
      });

      // Call the reservation API if there are pending appointments
      if (appointments?.length) {
        this.bookingService.reserveAppointments(appointments).subscribe({
          next: reservation => {
            if (!reservation?.success) {
              LoggerService.warn('[Debug] Failed to reserve an appointment');
            } else {
              // Mark all reserved items as confirmed
              this.bookingService.selectedBookingData.cartItems.forEach(
                appointment => (appointment.isAppointmentReserved = true)
              );
            }
          },
          error: (error: HttpErrorResponse) => {
            LoggerService.warn('[Debug] Failed to reserve an appointment');
            LoggerService.error(error);
          }
        });
      }
    }
  }

  protected increment(): void {
    this.counter++;
  }

  protected decrement(): void {
    this.counter--;
  }

  protected showOnePageUI(view: string): boolean {
    // [hidden_booking_steps] A hidden step must NOT persist in the one-page stack. Opting it out
    // reverts it to transient mount-on-active behaviour so its ngOnInit auto-skip re-fires in BOTH
    // directions (forward and backward) — the fix for the one-page back-navigation trap.
    // Exception: once a hidden step has FAILED to resolve its forced value it un-hides and renders
    // normally (see isHiddenStepSuppressed) — it must then behave as a normal one-page step so it
    // stays editable in the stack, instead of vanishing after the customer passes it.
    if (this.bookingService.isStepHidden(view) && !this.bookingService.hiddenStepResolutionFailed.has(view)) {
      return false;
    }
    if (this.widgetService.widgetConf?.context?.enable_one_page_ui) {
      if (this.selectedViewPrecedence >= this.sectionPrecedence[view]) {
        return true;
      }
      return false;
    }
    return false;
  }

  /**
   * [auto_load_booking_questions_placement=personal_form — review fix Finding F1] Gate for the
   * dedicated AFTER-questions view (BOOKING_QUESTIONS_VIEW_AFTER). Centralizes the template's
   * boolean (rather than an inline `@if` expression) so it's unit-testable in isolation: under
   * enable_one_page_ui + placement=personal_form, showOnePageUI(BOOKING_QUESTIONS_VIEW_AFTER) can
   * become true once selectedViewPrecedence reaches PERSONAL_FORM_VIEW (precedence 8 > 7) — without
   * this guard the dedicated AFTER step would co-mount alongside the personal form that already
   * embeds the very same auto-loaded questions (duplicate controls).
   *
   * Before-mode no longer suppresses the AFTER step when auto-load is ON: in the
   * combined mode the before-step asks the manual context questions and the AFTER step renders
   * the service-resolved remainder (getPlacementTargetQuestionIds()). Before-mode + auto-load
   * OFF keeps the original before-only behaviour (no AFTER step — the before-step asked everything);
   * placement=personal_form keeps suppressing it (the personal form embeds those questions).
   */
  protected showAfterQuestionsView(): boolean {
    if (this.bookingService.isAutoLoadInPersonalForm()) {
      return false;
    }
    return !this.bookingService.isBeforeQuestionsMode() || this.bookingService.isAutoLoadBookingQuestionsEnabled();
  }

  /**
   * [hidden_booking_steps] Suppress the brief transient render ("flash") of a hidden step while it
   * resolves its forced value and auto-skips. If the step could NOT resolve, it un-hides and renders
   * normally (safety fallback — never an invisible dead-end).
   */
  protected isHiddenStepSuppressed(view: string): boolean {
    return this.bookingService.isStepHidden(view) && !this.bookingService.hiddenStepResolutionFailed.has(view);
  }

  /**
   * A step's section, looked up inside this component because the web component can render in a shadow
   * root. Null once the step is no longer active, or while a hidden step is still resolving.
   */
  private findScrollableSection(view: BookingPageViews): HTMLElement | null {
    if (this.selectedView !== view || this.isHiddenStepSuppressed(view)) {
      return null;
    }
    return this.elementRef.nativeElement.querySelector<HTMLElement>('[id="' + view + '"]');
  }

  protected allowEditActivePage(view: BookingPageViews): boolean {
    if (this.showOnePageUI(view)) {
      if (this.selectedView === view) {
        return true;
      }
      return false;
    } else {
      return true;
    }
  }

  private getTimezones(): void {
    this.timezoneService.getTimezones()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: timezones => {
          this.timezones = timezones?.data ?? [];
          const localTimezone = this.timezoneService.getLocalTimezone();
          const timezone = this.timezones.find(timezone => timezone.zone_group.includes(localTimezone))?.zone;
          if (timezone) {
            this.timezoneService.localTimezone = timezone;
          }
          this.bookingService.getDateByPlaceholder();
        },
      });
  }

  protected onTimezoneChangeEvent(event: string): void {
    this.bookingService.selectedBookingData.timezone = event;
    this.customEventService.emptyCartEvent.emit();
    this.customEventService.reloadCalendarEvent.emit();
    // this.customEventService.refreshCalendar.emit();
  }
}
